import AppActionMenu from '../../components/AppActionMenu'
import AppDropdown from '../../components/AppDropdown'
import AppPagination from '../../components/AppPagination'
import { useNotificationScreen } from './hooks/useNotificationScreen'

function severityPillClass(severity) {
  const normalized = String(severity || '').toUpperCase()
  if (normalized === 'WARNING' || normalized === 'OVERDUE') return 'bg-error-container text-on-error-container'
  if (normalized === 'DUE_TODAY') return 'bg-amber-100 text-amber-700'
  if (normalized === 'DUE_SOON') return 'bg-primary-fixed text-primary'
  return 'bg-surface-container text-on-surface-variant'
}

function iconForSeverity(severity) {
  const normalized = String(severity || '').toUpperCase()
  if (normalized === 'WARNING' || normalized === 'OVERDUE') return 'priority_high'
  if (normalized === 'DUE_TODAY' || normalized === 'DUE_SOON') return 'schedule'
  return 'info'
}

function titleForModule(row) {
  return row?.category || row?.entityType || 'Notification'
}

export default function NotificationCenterScreen() {
  const { state, actions } = useNotificationScreen()

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-8">
        <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Inbox</h1>
            <div className="mt-2 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.13em] text-on-surface-variant">
              <span>Notification Center</span>
              <span className="h-1 w-1 rounded-full bg-primary" />
              <span>{state.summary.unreadCount} unread</span>
              <span className="h-1 w-1 rounded-full bg-primary" />
              <span>{state.summary.archivedCount} archived</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={actions.markFilteredRead}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-on-primary transition hover:brightness-95"
            >
              <span className="material-symbols-outlined text-base">done_all</span>
              <span>Mark filtered as read</span>
            </button>
            <button
              type="button"
              onClick={actions.archiveFiltered}
              className="inline-flex items-center gap-2 rounded-xl bg-surface-container-high px-4 py-2.5 text-sm font-bold text-on-surface transition hover:brightness-95"
            >
              <span className="material-symbols-outlined text-base">archive</span>
              <span>Archive filtered</span>
            </button>
          </div>
        </div>

        <section className="mb-4 flex flex-wrap items-end gap-3 rounded-lg bg-surface-container-low p-3">
          <label className="relative min-w-[260px] flex-1">
            <span className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-outline">
              search
            </span>
            <input
              type="text"
              value={state.searchText}
              onChange={(event) => actions.setSearchText(event.target.value)}
              placeholder="Search notifications..."
              className="w-full rounded-xl border-none bg-surface-container-lowest py-3 pl-12 pr-4 text-sm text-on-surface outline-none placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/10"
            />
          </label>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <div className="flex items-center gap-1 rounded-xl bg-surface-container-lowest p-1">
              <button
                type="button"
                onClick={() => actions.setStatusFilter('unread')}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-[0.11em] ${
                  state.statusFilter === 'unread' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'
                }`}
              >
                Unread
              </button>
              <button
                type="button"
                onClick={() => actions.setStatusFilter('all')}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-[0.11em] ${
                  state.statusFilter === 'all' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'
                }`}
              >
                All
              </button>
            </div>

            <label className="grid min-w-[190px] gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Priority</span>
              <AppDropdown
                value={state.severityFilter}
                onChange={actions.setSeverityFilter}
                triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
                options={state.severityOptions}
              />
            </label>

            <label className="grid min-w-[190px] gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Module</span>
              <AppDropdown
                value={state.moduleFilter}
                onChange={actions.setModuleFilter}
                triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
                options={state.moduleOptions}
              />
            </label>

            <label className="grid min-w-[190px] gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Date Range</span>
              <AppDropdown
                value={state.datePreset}
                onChange={actions.setDatePreset}
                triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
                options={[
                  { value: '7d', label: 'Last 7 Days' },
                  { value: '30d', label: 'Last 30 Days' },
                  { value: '90d', label: 'Last 90 Days' },
                  { value: 'all', label: 'All Time' },
                ]}
              />
            </label>

            <label className="grid min-w-[190px] gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Scope</span>
              <AppDropdown
                value={state.scopeFilter}
                onChange={actions.setScopeFilter}
                triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
                options={[
                  { value: 'active', label: 'Active Only' },
                  { value: 'include_archived', label: 'Include Archived' },
                  { value: 'archived', label: 'Archived Only' },
                ]}
              />
            </label>
          </div>
        </section>

        {state.error ? (
          <div className="mb-3 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
        ) : null}
        {state.actionMessage ? (
          <div className="mb-3 rounded-xl border border-primary/20 bg-primary-fixed px-4 py-3 text-sm text-primary">{state.actionMessage}</div>
        ) : null}
      </header>

      <section className="overflow-hidden rounded-2xl bg-surface-container-lowest">
        <div className="divide-y divide-surface-container">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant">Loading notifications...</div>
          ) : state.rows.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant">No notifications found for selected filters.</div>
          ) : (
            state.rows.map((row) => (
              <article key={row.id} className={`relative flex items-start gap-5 px-6 py-5 transition-colors hover:bg-surface-container-low ${row.isRead ? 'opacity-80' : ''}`}>
                <div className={`mt-1 inline-flex h-11 w-11 items-center justify-center rounded-full ${severityPillClass(row.severity)}`}>
                  <span className="material-symbols-outlined text-base">{iconForSeverity(row.severity)}</span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="truncate text-base font-bold text-on-surface">{row.title || 'Notification'}</h3>
                    <div className="flex items-center gap-2">
                      {!row.isRead ? <span className="h-2 w-2 rounded-full bg-primary" title="Unread" /> : null}
                      {row.isArchive ? <span className="h-2 w-2 rounded-full bg-on-surface-variant" title="Archived" /> : null}
                      <span className="text-xs font-bold text-on-surface-variant">{row.relativeTime}</span>
                    </div>
                  </div>

                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="rounded bg-surface-container px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
                      {titleForModule(row)}
                    </span>
                    <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${severityPillClass(row.severity)}`}>
                      {row.severity || 'INFO'}
                    </span>
                    {row.entityType ? (
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
                        {row.entityType} {row.entityId ? `#${row.entityId}` : ''}
                      </span>
                    ) : null}
                  </div>

                  <p className="text-sm text-on-surface-variant">{row.message}</p>
                  {row.deadlineAt ? (
                    <p className="mt-1 text-xs font-medium text-on-surface-variant">Deadline: {new Date(row.deadlineAt).toLocaleString()}</p>
                  ) : null}
                </div>

                <AppActionMenu
                  triggerLabel=""
                  triggerIcon="more_vert"
                  showChevron={false}
                  triggerClassName="h-8 w-8 rounded-md bg-transparent p-0 text-on-surface-variant hover:bg-surface-container-high hover:text-primary"
                  menuClassName="min-w-[180px]"
                  disabled={state.actionBusyId === row.id}
                  items={[
                    ...(row.isRead
                      ? []
                      : [{ key: 'mark_read', label: 'Mark as Read', icon: 'done' }]),
                    ...(!row.isArchive
                      ? [{ key: 'archive', label: 'Archive', icon: 'archive' }]
                      : [{ key: 'unarchive', label: 'Unarchive', icon: 'unarchive' }]),
                  ]}
                  onSelect={(key) => {
                    if (key === 'mark_read') actions.markSingleRead(row.id)
                    if (key === 'archive') actions.archiveSingle(row.id)
                    if (key === 'unarchive') actions.unarchiveSingle(row.id)
                  }}
                />
              </article>
            ))
          )}
        </div>

        <div className="flex items-center justify-between bg-surface-container-low px-6 py-4">
          <p className="text-sm font-medium text-on-surface-variant">
            Showing <span className="font-bold text-on-surface">{state.rangeStart}-{state.rangeEnd}</span> of {state.totalCount} notifications
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
